import { useState } from "react";
import api from "../api/client";
import { useExpenseOptions } from "../context/ExpenseOptionsContext";

const CSS = `
.sr-title { font-size:18px; font-weight:700; color:#0d1a36; margin-bottom:24px; }

.sr-filters { background:#ffffff; border:1px solid #c0d0eb; border-radius:10px; padding:20px; margin-bottom:20px; }
.sr-filters-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:12px; margin-bottom:16px; }
.sr-field { display:flex; flex-direction:column; gap:5px; }
.sr-label { font-size:11px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:#6080aa; }
.sr-input  { background:#f4f8ff; border:1px solid #d5e3f5; border-radius:7px; padding:9px 12px;
  font-size:13px; color:#0d1a36; outline:none; font-family:inherit; transition:border-color .15s; }
.sr-input:focus  { border-color:#059669; }
.sr-select { background:#f4f8ff; border:1px solid #d5e3f5; border-radius:7px; padding:9px 12px;
  font-size:13px; color:#0d1a36; outline:none; font-family:inherit; cursor:pointer; }
.sr-select:focus { border-color:#059669; }
.sr-actions { display:flex; gap:10px; }
.sr-btn { display:inline-flex; align-items:center; gap:6px; padding:9px 18px; border-radius:7px;
  border:none; cursor:pointer; font-size:13px; font-weight:600; transition:opacity .15s; font-family:inherit; }
.sr-btn:disabled { opacity:.4; cursor:not-allowed; }
.sr-btn:not(:disabled):hover { opacity:.88; }
.sr-btn-primary { background:#059669; color:#f0fdf4; }
.sr-btn-ghost   { background:none; border:1px solid #c0d0eb; color:#2a4a78; }
.sr-spinner-btn { display:inline-block; width:14px; height:14px; border:2px solid rgba(0,0,0,.2);
  border-top-color:#000; border-radius:50%; animation:srspin .6s linear infinite; }
@keyframes srspin { to{ transform:rotate(360deg); } }

.sr-results-header { font-size:12px; color:#6080aa; margin-bottom:12px; font-weight:600; }

.sr-table-wrap { overflow-x:auto; }
.sr-table { width:100%; border-collapse:collapse; }
.sr-table th { font-size:11px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:#6080aa;
  padding:0 14px 10px; text-align:left; border-bottom:1px solid #c0d0eb; white-space:nowrap; }
.sr-table td { padding:10px 14px; border-bottom:1px solid #d5e3f5; font-size:13px; vertical-align:middle; }
.sr-table tr:last-child td { border-bottom:none; }
.sr-table tr:hover td { background:#f4f8ff; }
.sr-td-name  { color:#0d1a36; font-weight:500; }
.sr-td-shop  { color:#2a4a78; }
.sr-td-price { color:#2563eb; font-weight:600; font-variant-numeric:tabular-nums; }
.sr-td-cat   { color:#2a4a78; }
.sr-td-date  { color:#6080aa; white-space:nowrap; }
.sr-empty    { font-size:13px; color:#6080aa; padding:20px 0; }
`;

const BLANK = { item_name:"", shop_name:"", category:"", date_from:"", date_to:"", min_price:"", max_price:"" };

export default function Search() {
  const { categories } = useExpenseOptions();
  const [form,    setForm]    = useState(BLANK);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const search = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const r = await api.get(`/expense/search?${params}`);
      setResults(r.data);
    } catch { setResults([]); }
    finally { setLoading(false); }
  };

  const reset = () => { setForm(BLANK); setResults(null); };

  return (
    <>
      <style>{CSS}</style>
      <div className="sr-title">🔎 Search Expenses</div>

      <div className="sr-filters">
        <div className="sr-filters-grid">
          <div className="sr-field">
            <label className="sr-label">Item Name</label>
            <input className="sr-input" placeholder="e.g. milk" value={form.item_name}
                   onChange={e=>set("item_name",e.target.value)}
                   onKeyDown={e=>e.key==="Enter"&&search()} />
          </div>
          <div className="sr-field">
            <label className="sr-label">Shop</label>
            <input className="sr-input" placeholder="e.g. Lulu" value={form.shop_name}
                   onChange={e=>set("shop_name",e.target.value)}
                   onKeyDown={e=>e.key==="Enter"&&search()} />
          </div>
          <div className="sr-field">
            <label className="sr-label">Category</label>
            <select className="sr-select" value={form.category} onChange={e=>set("category",e.target.value)}>
              <option value="">All</option>
              {categories.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="sr-field">
            <label className="sr-label">From Date</label>
            <input className="sr-input" type="date" value={form.date_from} onChange={e=>set("date_from",e.target.value)} />
          </div>
          <div className="sr-field">
            <label className="sr-label">To Date</label>
            <input className="sr-input" type="date" value={form.date_to} onChange={e=>set("date_to",e.target.value)} />
          </div>
          <div className="sr-field">
            <label className="sr-label">Min Price (OMR)</label>
            <input className="sr-input" type="number" step="0.001" min="0" placeholder="0.000"
                   value={form.min_price} onChange={e=>set("min_price",e.target.value)} />
          </div>
          <div className="sr-field">
            <label className="sr-label">Max Price (OMR)</label>
            <input className="sr-input" type="number" step="0.001" min="0" placeholder="999.000"
                   value={form.max_price} onChange={e=>set("max_price",e.target.value)} />
          </div>
        </div>
        <div className="sr-actions">
          <button className="sr-btn sr-btn-primary" onClick={search} disabled={loading}>
            {loading ? <><span className="sr-spinner-btn"/>Searching…</> : "🔍 Search"}
          </button>
          <button className="sr-btn sr-btn-ghost" onClick={reset}>Clear</button>
        </div>
      </div>

      {results !== null && (
        <>
          <div className="sr-results-header">
            {results.length === 0 ? "No results found." : `${results.length} result${results.length===1?"":"s"} found`}
          </div>
          {results.length > 0 && (
            <div className="sr-table-wrap">
              <table className="sr-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Shop</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Qty</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i}>
                      <td className="sr-td-name">{r.item_name || r.name}</td>
                      <td className="sr-td-shop">{r.shop_name}</td>
                      <td className="sr-td-cat">{r.category}</td>
                      <td className="sr-td-price">OMR {parseFloat(r.price||0).toFixed(3)}</td>
                      <td className="sr-td-shop">{r.quantity||1}</td>
                      <td className="sr-td-date">{r.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}
